import { createRouter, createWebHistory } from 'vue-router'
import { apiFetch, getAuthToken, requireAuthHeaders } from './utils/api'

// Lazy load all components for better code splitting
const Login = () => import('./components/UserLogin.vue')
const Account = () => import('./components/UserAccount.vue')
const PandaRecentMessages = () => import('./components/RecentMessages.vue')
const MessageEditing = () => import('./components/MessageEditing.vue')
const SupervisorPage = () => import('./components/SupervisorPage.vue')
const ObserverPage = () => import('./components/ObserverPage.vue')
const DpoMessages = () => import('./components/DpoMessages.vue')

// Supervisor tab components (lazy-loaded as children)
const supervisorChildren = [
  {
    path: '',
    redirect: { name: 'supervisor-traffic' }
  },
  {
    path: 'traffic',
    name: 'supervisor-traffic',
    component: () => import('./components/TrafficPage.vue'),
    meta: { tabName: 'traffic', requiresPrivilegedConsole: true }
  },
  {
    path: 'pipeline',
    name: 'supervisor-pipeline',
    component: () => import('./components/PipelineEditorPage.vue'),
    meta: { tabName: 'pipeline' }
  },
  {
    path: 'voice-studio',
    name: 'supervisor-voiceStudio',
    component: () => import('./components/VoiceStudioPage.vue'),
    meta: { tabName: 'voiceStudio' }
  },
  {
    path: 'view-recent',
    name: 'supervisor-viewRecent',
    component: () => import('./components/ViewRecentPage.vue'),
    meta: { tabName: 'viewRecent', requiresPrivilegedConsole: true }
  },
  {
    path: 'ai-config',
    name: 'supervisor-aiConfig',
    component: () => import('./components/AiConfigPage.vue'),
    meta: { tabName: 'aiConfig' }
  },
  {
    path: 'sampler-config',
    name: 'supervisor-samplerConfig',
    component: () => import('./components/SamplerConfigPage.vue'),
    meta: { tabName: 'samplerConfig' }
  },
  {
    path: 'news-config',
    name: 'supervisor-newsConfig',
    component: () => import('./components/NewsConfigPage.vue'),
    meta: { tabName: 'newsConfig' }
  },
  {
    path: 'ngram-heat',
    name: 'supervisor-ngramHeat',
    component: () => import('./components/NgramHeatPage.vue'),
    meta: { tabName: 'ngramHeat' }
  },
  {
    path: 'user-stats',
    name: 'supervisor-userStats',
    component: () => import('./components/UserStatistics.vue'),
    meta: { tabName: 'userStats' }
  },
  {
    path: 'pokes',
    name: 'supervisor-pokes',
    component: () => import('./components/PokesPage.vue'),
    meta: { tabName: 'pokes' }
  },
  {
    path: 'dpo-stats',
    name: 'supervisor-dpoStats',
    component: () => import('./components/DpoStatsPage.vue'),
    meta: { tabName: 'dpoStats' }
  },
  {
    path: 'weekly-dpo-stats',
    name: 'supervisor-weeklyDpoStats',
    component: () => import('./components/WeeklyDpoStats.vue'),
    meta: { tabName: 'weeklyDpoStats' }
  },
  {
    path: 'operator-stats',
    name: 'supervisor-operatorStats',
    component: () => import('./components/OperatorStatsPage.vue'),
    meta: { tabName: 'operatorStats' }
  },
  {
    path: 'monthly-earnings',
    name: 'supervisor-monthlyEarnings',
    component: () => import('./components/MonthlyEarningsPage.vue'),
    meta: { tabName: 'monthlyEarnings' }
  },
  {
    path: 'search',
    name: 'supervisor-search',
    component: () => import('./components/SearchPage.vue'),
    meta: { tabName: 'search', requiresPrivilegedConsole: true }
  },
  {
    path: 'dataset-report',
    name: 'supervisor-datasetReport',
    component: () => import('./components/DatasetStatistics.vue'),
    meta: { tabName: 'datasetReport' }
  },
  // Developer-only tabs
  {
    path: 'dev-dashboard',
    name: 'supervisor-devDashboard',
    component: () => import('./components/DeveloperDashboard.vue'),
    meta: { tabName: 'devDashboard', requiresDeveloper: true }
  },
  {
    path: 'client-policy',
    name: 'supervisor-clientPolicy',
    component: () => import('./components/ClientPolicyPage.vue'),
    meta: { tabName: 'clientPolicy', requiresDeveloper: true }
  },
  {
    path: 'model-templates',
    name: 'supervisor-modelTemplates',
    component: () => import('./components/ModelTemplatesPage.vue'),
    meta: { tabName: 'modelTemplates', requiresDeveloper: true }
  },
  {
    path: 'dataset-generation',
    name: 'supervisor-datasetGeneration',
    component: () => import('./components/DatasetGenerationPage.vue'),
    meta: { tabName: 'datasetGeneration', requiresDeveloper: true }
  },
  {
    path: 'training',
    name: 'supervisor-training',
    component: () => import('./components/TrainingPage.vue'),
    meta: { tabName: 'training', requiresDeveloper: true }
  },
  {
    path: 'testing',
    name: 'supervisor-testing',
    component: () => import('./components/TestingPage.vue'),
    meta: { tabName: 'testing', requiresDeveloper: true }
  },
  {
    path: 'translation-profiles',
    name: 'supervisor-translationProfiles',
    component: () => import('./components/TranslationProfilesPage.vue'),
    meta: { tabName: 'translationProfiles', requiresDeveloper: true }
  },
  {
    path: 'chat-explorer',
    name: 'supervisor-chatExplorer',
    component: () => import('./components/ChatExplorerPage.vue'),
    meta: { tabName: 'chatExplorer', requiresDeveloper: true }
  },
  {
    path: 'image-explorer',
    name: 'supervisor-imageExplorer',
    component: () => import('./components/ImageExplorerPage.vue'),
    meta: { tabName: 'imageExplorer', requiresDeveloper: true }
  },
  {
    path: 'memory-explorer',
    name: 'supervisor-memoryExplorer',
    component: () => import('./components/MemoryExplorerPage.vue'),
    meta: { tabName: 'memoryExplorer', requiresDeveloper: true }
  },
  {
    path: 'chat-viewer',
    name: 'supervisor-chatViewer',
    component: () => import('./components/ConversationSimulatorPage.vue'),
    meta: { tabName: 'chatViewer', requiresDeveloper: true }
  },
  {
    path: 'future-plans',
    name: 'supervisor-futurePlans',
    component: () => import('./components/FuturePlansPage.vue'),
    meta: { tabName: 'futurePlans', requiresDeveloper: true }
  },
  {
    path: 'business-ideas',
    name: 'supervisor-businessIdeas',
    component: () => import('./components/BusinessIdeasPage.vue'),
    meta: { tabName: 'businessIdeas', requiresDeveloper: true }
  },
  {
    path: 'inference-endpoints',
    name: 'supervisor-inferenceEndpoints',
    component: () => import('./components/InferenceEndpointsPage.vue'),
    meta: { tabName: 'inferenceEndpoints', requiresDeveloper: true }
  },
  {
    path: 'cluster-status',
    name: 'supervisor-clusterStatus',
    component: () => import('./components/ClusterStatusPage.vue'),
    meta: { tabName: 'clusterStatus', requiresDeveloper: true }
  },
  {
    path: 'payload-schema',
    name: 'supervisor-payloadSchema',
    component: () => import('./components/PayloadSchemaPage.vue'),
    meta: { tabName: 'payloadSchema', requiresDeveloper: true }
  },
  {
    path: 'poke-testing',
    name: 'supervisor-pokeTesting',
    component: () => import('./components/PokeTestingPage.vue'),
    meta: { tabName: 'pokeTesting', requiresDeveloper: true }
  },
  {
    path: 'test-suite',
    name: 'supervisor-testSuite',
    component: () => import('./components/ChatTestSuitePage.vue'),
    meta: { tabName: 'testSuite', requiresDeveloper: true }
  },
  {
    path: 'replay-comparison',
    name: 'supervisor-replayComparison',
    component: () => import('./components/ReplayComparisonPage.vue'),
    meta: { tabName: 'replayComparison', requiresDeveloper: true }
  },
  {
    path: 'news-explorer',
    name: 'supervisor-newsExplorer',
    component: () => import('./components/NewsExplorerPage.vue'),
    meta: { tabName: 'newsExplorer', requiresDeveloper: true }
  },
  {
    path: 'dataset-builder',
    name: 'supervisor-datasetBuilder',
    component: () => import('./components/DatasetBuilderPage.vue'),
    meta: { tabName: 'datasetBuilder', requiresDeveloper: true }
  },
  {
    path: 'account',
    name: 'supervisor-account',
    component: () => import('./components/SupervisorAccountPage.vue'),
    meta: { tabName: 'account' }
  }
]

const routes = [
  {
    path: '/',
    name: 'login',
    component: Login
  },
  {
    path: '/login',
    redirect: '/'
  },
  {
    path: '/account',
    redirect: '/user/account'
  },
  {
    path: '/user/account',
    name: 'user-account',
    component: Account
  },
  {
    path: '/recent-messages/:username',
    name: 'recentMessages',
    component: PandaRecentMessages,
    props: true
  },
  {
    path: '/messageEditing',
    name: 'messageEditing',
    component: MessageEditing
  },
  // Supervisor page with nested tab routes
  {
    path: '/supervisor',
    component: SupervisorPage,
    meta: { requiresPrivilegedConsole: true },
    children: supervisorChildren
  },
  // Legacy redirect for backward compatibility
  {
    path: '/supervisorPage',
    redirect: () => {
      const savedTab = localStorage.getItem('currentTab')
      const validTabs = supervisorChildren
        .filter(c => c.name)
        .map(c => c.meta?.tabName)
      if (savedTab && validTabs.includes(savedTab)) {
        return { name: `supervisor-${savedTab}` }
      }
      return { name: 'supervisor-traffic' }
    }
  },
  {
    path: '/observerPage',
    name: 'observer',
    component: ObserverPage
  },
  {
    path: '/dpo-messages',
    name: 'dpoMessages',
    component: DpoMessages,
    props: route => ({
      username: route.query.username,
      startDate: route.query.startDate,
      endDate: route.query.endDate
    })
  },
  {
    path: '/training',
    name: 'training',
    component: () => import('./components/TrainingPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/testing',
    name: 'testing',
    component: () => import('./components/TestingPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/translation-profiles',
    name: 'translationProfiles',
    component: () => import('./components/TranslationProfilesPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/chat-explorer',
    name: 'chatExplorer',
    component: () => import('./components/ChatExplorerPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/image-explorer',
    name: 'imageExplorer',
    component: () => import('./components/ImageExplorerPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/memory-explorer',
    name: 'memoryExplorer',
    component: () => import('./components/MemoryExplorerPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/chat-viewer',
    name: 'chatViewer',
    component: () => import('./components/ConversationSimulatorPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/ai-config',
    name: 'aiConfig',
    component: () => import('./components/AiConfigPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/client-policy',
    name: 'clientPolicy',
    component: () => import('./components/ClientPolicyPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/dataset-generation',
    name: 'datasetGeneration',
    component: () => import('./components/DatasetGenerationPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/model-templates',
    name: 'modelTemplates',
    component: () => import('./components/ModelTemplatesPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/pokes',
    name: 'pokes',
    component: () => import('./components/PokesPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/sampler-config',
    name: 'samplerConfig',
    component: () => import('./components/SamplerConfigPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/news-config',
    name: 'newsConfig',
    component: () => import('./components/NewsConfigPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/search',
    name: 'search',
    component: () => import('./components/SearchPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/traffic',
    name: 'traffic',
    component: () => import('./components/TrafficPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/view-recent',
    name: 'viewRecent',
    component: () => import('./components/ViewRecentPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/weekly-dpo-stats',
    name: 'weeklyDpoStats',
    component: () => import('./components/WeeklyDpoStats.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/ngram-heat',
    name: 'ngramHeat',
    component: () => import('./components/NgramHeatPage.vue'),
    meta: { requiresPrivilegedConsole: true }
  },
  {
    path: '/cluster-status',
    name: 'clusterStatus',
    component: () => import('./components/ClusterStatusPage.vue'),
    meta: { requiresDeveloper: true }
  },
  {
    path: '/payload-schema',
    name: 'payloadSchema',
    component: () => import('./components/PayloadSchemaPage.vue'),
    meta: { requiresDeveloper: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Cache current-user role flags to avoid repeated API calls during navigation.
let currentUserCache = null
let currentUserPromise = null
let currentUserToken = null

async function fetchCurrentUser() {
  const token = getAuthToken()

  // Return cached value if available
  if (currentUserCache !== null && currentUserToken === token) {
    return currentUserCache
  }

  // Return existing promise if a check is already in progress
  if (currentUserPromise && currentUserToken === token) {
    return currentUserPromise
  }

  if (!token) {
    currentUserCache = null
    currentUserToken = null
    return null
  }

  currentUserToken = token
  currentUserPromise = apiFetch('/api/users/current_user/', {
    headers: requireAuthHeaders({
      'Content-Type': 'application/json',
    })
  })
    .then(response => {
      if (response.ok) {
        return response.json()
      }
      throw new Error('Failed to fetch user')
    })
    .then(user => {
      currentUserCache = user
      return currentUserCache
    })
    .catch(() => {
      currentUserCache = null
      currentUserToken = null
      return null
    })
    .finally(() => {
      currentUserPromise = null
    })

  return currentUserPromise
}

async function checkDeveloperStatus() {
  const user = await fetchCurrentUser()
  return Boolean(user && user.is_developer)
}

async function checkPrivilegedConsoleStatus() {
  const user = await fetchCurrentUser()
  return Boolean(user && (user.is_supervisor || user.is_observer || user.is_developer))
}

// Reset cache on logout (call this from logout logic)
export function resetDeveloperCache() {
  currentUserCache = null
  currentUserPromise = null
  currentUserToken = null
}

// Navigation guard for auth and developer-only routes
router.beforeEach(async (to, from, next) => {
  const token = getAuthToken()
  const isAuthenticated = !!token

  // If going to login page and already authenticated, redirect to supervisor
  if (to.name === 'login' && isAuthenticated) {
    const savedTab = localStorage.getItem('currentTab')
    const validTabs = supervisorChildren
      .filter(c => c.name)
      .map(c => c.meta?.tabName)
    if (savedTab && validTabs.includes(savedTab)) {
      return next({ name: `supervisor-${savedTab}` })
    }
    return next({ name: 'supervisor-traffic' })
  }

  // Protected routes that require authentication
  const protectedRoutes = [
    'user-account',
    'recentMessages',
    'observer',
    'dpoMessages',
    'messageEditing',
    'training',
    'translationProfiles',
    'chatExplorer',
    'imageExplorer',
    'chatViewer',
    'aiConfig',
    'clientPolicy',
    'datasetGeneration',
    'modelTemplates',
    'pokes',
    'samplerConfig',
    'newsConfig',
    'search',
    'traffic',
    'weeklyDpoStats',
    'ngramHeat',
  ]
  const isProtectedRoute = Boolean(
    to.meta?.requiresDeveloper
    || to.meta?.requiresPrivilegedConsole
    || protectedRoutes.includes(to.name)
    || to.path.startsWith('/supervisor')
  )

  // If accessing protected route without auth, redirect to login with next param
  if (isProtectedRoute && !isAuthenticated) {
    return next({ name: 'login', query: { next: to.fullPath } })
  }

  // Check if route requires developer permission
  if (to.meta?.requiresDeveloper) {
    const isDeveloper = await checkDeveloperStatus()

    if (!isDeveloper) {
      // Redirect non-developers to default tab
      return next({ name: 'supervisor-traffic' })
    }
  }

  if (to.meta?.requiresPrivilegedConsole) {
    const isPrivileged = await checkPrivilegedConsoleStatus()

    if (!isPrivileged) {
      return next({ name: 'user-account' })
    }
  }

  next()
})

export default router
